import { useKycVerificationContext } from "../../Contexts/KycVerificationContext";
import { CameraAltOutlined } from "@mui/icons-material";
import React, { useRef, useState } from "react";
import { IconButton } from "@mui/material";

function CaptureId() {
  const { setErrorMessage, errorMessage, setFormData, formData, isMobile } =
    useKycVerificationContext();

  const [videoStartedFront, setVideoStartedFront] = useState(false);
  const [videoStartedBack, setVideoStartedBack] = useState(false);
  const frontVideoRef = useRef(null);
  const backVideoRef = useRef(null);
  const canvasRef = useRef(null);

  const handleSetPhoto = (photo, side) => {
    const field = side === "back" ? "backID" : "frontID";
    setFormData((prevData) => ({ ...prevData, [field]: photo }));
    setErrorMessage("");
  };

  const handleChooseFile = (e, side) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();

      reader.onloadend = () => {
        const img = new Image();
        img.src = reader.result;

        img.onload = () => {
          let { width, height } = img;

          // Constrain the dimensions
          const minDimension = 100;
          const maxDimension = 1000;

          if (width < minDimension || height < minDimension) {
            const scaleFactor = Math.max(
              minDimension / width,
              minDimension / height
            );
            width = Math.round(width * scaleFactor);
            height = Math.round(height * scaleFactor);
          }
          if (width > maxDimension || height > maxDimension) {
            const scaleFactor = Math.min(
              maxDimension / width,
              maxDimension / height
            );
            width = Math.round(width * scaleFactor);
            height = Math.round(height * scaleFactor);
          }

          // Create canvas and draw image
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");
          canvas.width = width;
          canvas.height = height;
          ctx.drawImage(img, 0, 0, width, height);

          // Function to check file size and adjust quality
          const checkFileSize = (quality, callback) => {
            const dataURL = canvas.toDataURL("image/jpeg", quality);
            const blob = dataURLToBlob(dataURL);

            if (blob.size <= 1 * 1024 * 1024) {
              // File size is <= 1MB
              callback(dataURL);
            } else if (quality > 0.1) {
              // Reduce quality and check again
              checkFileSize(quality - 0.1, callback);
            } else {
              callback(dataURL); // Fallback to lowest quality
            }
          };

          // Helper function to convert dataURL to Blob
          const dataURLToBlob = (dataURL) => {
            const byteString = atob(dataURL.split(",")[1]);
            const mimeString = dataURL
              .split(",")[0]
              .split(":")[1]
              .split(";")[0];
            const ab = new ArrayBuffer(byteString.length);
            const ia = new Uint8Array(ab);
            for (let i = 0; i < byteString.length; i++) {
              ia[i] = byteString.charCodeAt(i);
            }
            return new Blob([ab], { type: mimeString });
          };

          // Start with high quality and adjust as needed
          checkFileSize(0.9, (optimizedImage) => {
            handleSetPhoto(optimizedImage, side);
          });
        };
      };

      reader.readAsDataURL(file);
    }
  };

  const startVideo = async (side) => {
    let videoRef = null;
    if (side === "front") {
      videoRef = frontVideoRef;
    }
    if (side === "back") {
      videoRef = backVideoRef;
    }
    try {
      if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment" },
        });
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.onloadedmetadata = () => {
            videoRef.current.play().catch((err) => {
              console.error("Error playing video:", err);
              setErrorMessage(
                "Error starting video playback. Please ensure your camera is accessible."
              );
            });
          };
          videoRef.current.onerror = (err) => {
            console.error("Video error:", err);
            setErrorMessage("Error loading video. Please try again.");
          };
          if (side === "front") {
            setVideoStartedFront(true);
          }
          if (side === "back") {
            setVideoStartedBack(true);
          }
        }
      } else {
        setErrorMessage("Media devices are not supported in your browser.");
      }
    } catch (err) {
      console.error("Error accessing camera:", err);
      if (err.name === "NotAllowedError") {
        setErrorMessage(
          "Camera access was denied. Please allow camera access."
        );
      } else if (err.name === "NotFoundError") {
        setErrorMessage("No camera device found.");
      } else {
        setErrorMessage(
          "Unable to access the camera. Please check your permissions."
        );
      }
    }
  };

  const handleCapture = async (side) => {
    let video = null;
    if (side === "front") {
      video = frontVideoRef.current;
    }
    if (side === "back") {
      video = backVideoRef.current;
    }

    if (video && canvasRef.current) {
      const canvas = canvasRef.current;
      const context = canvas.getContext("2d");

      // Capture the image
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      context.drawImage(video, 0, 0, canvas.width, canvas.height);

      // Resize the image if necessary
      const maxWidth = 1000;
      const maxHeight = 1000;
      const minDimension = 100;

      let { width, height } = canvas;
      if (width < minDimension || height < minDimension) {
        const scaleFactor = Math.max(
          minDimension / width,
          minDimension / height
        );
        width = Math.round(width * scaleFactor);
        height = Math.round(height * scaleFactor);
      }
      if (width > maxWidth || height > maxHeight) {
        const scaleFactor = Math.min(maxWidth / width, maxHeight / height);
        width = Math.round(width * scaleFactor);
        height = Math.round(height * scaleFactor);
      }

      // Resize canvas to the final dimensions
      const resizedCanvas = document.createElement("canvas");
      const resizedContext = resizedCanvas.getContext("2d");
      resizedCanvas.width = width;
      resizedCanvas.height = height;
      resizedContext.drawImage(canvas, 0, 0, width, height);

      // Function to check file size and adjust quality
      const checkFileSize = async (quality, callback) => {
        const dataURL = resizedCanvas.toDataURL("image/jpeg", quality);
        const blob = dataURLToBlob(dataURL);

        if (blob.size <= 1 * 1024 * 1024) {
          // File size is <= 1MB
          callback(dataURL);
        } else if (quality > 0.1) {
          // Reduce quality and check again
          checkFileSize(quality - 0.1, callback);
        } else {
          callback(dataURL); // Fallback to lowest quality
        }
      };

      // Helper function to convert dataURL to Blob
      const dataURLToBlob = (dataURL) => {
        const byteString = atob(dataURL.split(",")[1]);
        const mimeString = dataURL.split(",")[0].split(":")[1].split(";")[0];
        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);
        for (let i = 0; i < byteString.length; i++) {
          ia[i] = byteString.charCodeAt(i);
        }
        return new Blob([ab], { type: mimeString });
      };

      // Start with high quality and adjust as needed
      await checkFileSize(0.9, (optimizedImage) => {
        handleSetPhoto(optimizedImage, side);
      });

      // Stop the video stream
      const stream = video.srcObject;
      if (stream) {
        const tracks = stream.getTracks();
        tracks.forEach((track) => track.stop());
        if (side === "front") {
          setVideoStartedFront(false);
        }
        if (side === "back") {
          setVideoStartedBack(false);
        }
      }
    } else {
      setErrorMessage("Unable to capture image. Video is not available.");
    }
  };

  return (
    <div className="p-4">
      <p className="text-[#1F2328] text-xl font-bold mb-1">Capture ID</p>
      <p className="text-[#60656D] mb-4">
        Take a clear photo of the front and back of your ID.
      </p>
      {errorMessage && (
        <p className="font-bold text-red-600 mb-2">{errorMessage}</p>
      )}

      {/* Front ID Section */}
      <div>
        <p className="font-bold mb-2">Front</p>
        <div className="flex min-h-[200px] rounded-lg overflow-hidden relative bg-[#edf0f6] mb-4">
          {formData.frontID ? (
            <img src={formData.frontID} className="h-full" alt="frontID" />
          ) : !isMobile ? (
            <video ref={frontVideoRef} className="w-full" />
          ) : null}
          {isMobile && (
            <div>
              <input
                id="frontID"
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={(e) => handleChooseFile(e, "front")}
              />
              <label
                htmlFor="frontID"
                className="cursor-pointer flex items-center justify-center absolute bottom-2 right-2 w-10 h-10 z-1 bg-[#0947ab] text-white rounded-full"
              >
                <CameraAltOutlined />
              </label>
            </div>
          )}
          {!isMobile && formData.frontID === "" && (
            <div className="absolute bottom-2 right-2">
              {!videoStartedFront ? (
                <IconButton
                  onClick={() => startVideo("front")}
                  sx={{
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "48px",
                    height: "48px",
                    backgroundColor: "#0947ab",
                    color: "white",
                    borderRadius: "50%",
                    zIndex: 1,
                    "&:hover": {
                      backgroundColor: "#073a7d",
                    },
                  }}
                >
                  <CameraAltOutlined />
                </IconButton>
              ) : (
                <IconButton
                  onClick={() => handleCapture("front")}
                  sx={{
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "48px",
                    height: "48px",
                    backgroundColor: "#0947ab",
                    color: "white",
                    borderRadius: "50%",
                    zIndex: 1,
                    "&:hover": {
                      backgroundColor: "#073a7d",
                    },
                  }}
                >
                  <CameraAltOutlined />
                </IconButton>
              )}
            </div>
          )}
        </div>
      </div>
      {!isMobile && formData.frontID !== "" && (
        <div className="flex justify-center items-center mt-2">
          <button
            className="p-1 px-3 rounded-lg bg-[#f7f7f7] border shadow-xs"
            onClick={async () => {
              setFormData((prevData) => ({
                ...prevData,
                frontID: "",
              }));
              startVideo("front");
            }}
          >
            Retake Front ID
          </button>
        </div>
      )}
      {/* Back ID Section */}
      <div>
        <p className="font-bold mb-2">Back</p>
        <div className="flex min-h-[200px] rounded-lg overflow-hidden relative bg-[#edf0f6]">
          {formData.backID ? (
            <img src={formData.backID} className="w-full h-full" alt="back" />
          ) : !isMobile ? (
            <video ref={backVideoRef} className="w-full" />
          ) : null}
          {isMobile && (
            <div>
              <input
                id="backID"
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={(e) => handleChooseFile(e, "back")}
              />
              <label
                htmlFor="backID"
                className="cursor-pointer flex items-center justify-center absolute bottom-2 right-2 w-10 h-10 z-1 bg-[#0947ab] text-white rounded-full"
              >
                <CameraAltOutlined />
              </label>
            </div>
          )}
          {!isMobile && formData.backID === "" && (
            <div className="absolute bottom-2 right-2">
              {!videoStartedBack ? (
                <IconButton
                  onClick={() => startVideo("back")}
                  sx={{
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "48px",
                    height: "48px",
                    backgroundColor: "#0947ab",
                    color: "white",
                    borderRadius: "50%",
                    zIndex: 1,
                    "&:hover": {
                      backgroundColor: "#073a7d",
                    },
                  }}
                >
                  <CameraAltOutlined />
                </IconButton>
              ) : (
                <IconButton
                  onClick={() => handleCapture("back")}
                  sx={{
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "48px",
                    height: "48px",
                    backgroundColor: "#0947ab",
                    color: "white",
                    borderRadius: "50%",
                    zIndex: 1,
                    "&:hover": {
                      backgroundColor: "#073a7d",
                    },
                  }}
                >
                  <CameraAltOutlined />
                </IconButton>
              )}
            </div>
          )}
        </div>
      </div>

      <canvas ref={canvasRef} style={{ display: "none" }} />

      {!isMobile && formData.backID !== "" ? (
        <div className="flex justify-center items-center mt-2">
          <button
            className="p-1 px-3 rounded-lg bg-[#f7f7f7] border shadow-xs"
            onClick={async () => {
              setFormData((prevData) => ({
                ...prevData,
                backID: "",
              }));
              startVideo("back");
            }}
          >
            Retake Back ID
          </button>
        </div>
      ) : null}
    </div>
  );
}

export default CaptureId;
